import { Lock } from "lucide-react";
import SettingSection from "./SettingSection";
import ToggleSwitch from "./ToggleSwitch";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from '../../../../shared/common/ThemeContext';
import { fetchAuth } from '../../../../shared/common/api';
import { useTranslation } from "react-i18next";

const Security = () => {
  const { t } = useTranslation();
  const [twoFactor, setTwoFactor] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { isDarkMode } = useTheme();

  const mismatch = useMemo(
    () => newPassword.length > 0 && confirmNewPassword.length > 0 && newPassword !== confirmNewPassword,
    [newPassword, confirmNewPassword]
  );

  const handleChangePassword = async () => {
    if (mismatch) return;
    if (!currentPassword || !newPassword) {
      alert(t("pages.settings.password_required"));
      return;
    }
    try {
      setSubmitting(true);
      // ⬇️ route corrigée
      const response = await fetchAuth(`/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Error changing password");

      alert("✅ " + t("pages.settings.password_updated"));
      setShowPasswordPopup(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmNewPassword("");
      setPasswordError("");
    } catch (error) {
      
      alert(error.message || t("pages.settings.password_update_error"));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!newPassword || !confirmNewPassword) {
      setPasswordError("");
    } else if (newPassword !== confirmNewPassword) {
      setPasswordError(t("pages.settings.password_mismatch"));
    } else {
      setPasswordError("");
    }
  }, [newPassword, confirmNewPassword]);

  return (
    <SettingSection icon={Lock} title={t("pages.settings.security")} isDarkMode={isDarkMode}>
      <ToggleSwitch
        label={<span className={`${isDarkMode ? 'text-white' : 'text-black'}`}>{t("pages.settings.two_factor")}</span>}
        isOn={twoFactor}
        onToggle={() => setTwoFactor(!twoFactor)}
      />

      <div className='mt-4'>
        <button
          className='bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition duration-200'
          onClick={() => setShowPasswordPopup(true)}
        >
          {t("pages.settings.change_password")}
        </button>
      </div>

      {showPasswordPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className={`p-6 rounded-lg shadow-lg w-96 relative z-10 ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
            <h2 className="text-lg font-bold mb-4">{t("pages.settings.change_password")}</h2>
            <input
              type="password"
              placeholder={t("pages.settings.current_password")}
              className="w-full border text-gray-800 border-gray-300 rounded-lg p-2 mb-4"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={submitting}
            />
            <input
              type="password"
              placeholder={t("pages.settings.new_password")}
              className="w-full border text-gray-800 border-gray-300 rounded-lg p-2 mb-4"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={submitting}
            />
            <input
              type="password"
              placeholder={t("pages.settings.confirm_password")}
              className="w-full border text-gray-800 border-gray-300 rounded-lg p-2 mb-2"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              disabled={submitting}
            />
            {passwordError && <p className="text-red-500 text-sm mb-3">{passwordError}</p>}

            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition disabled:opacity-60"
                onClick={() => setShowPasswordPopup(false)}
                disabled={submitting}
              >
                {t("common.cancel")}
              </button>
              <button
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
                onClick={handleChangePassword}
                disabled={submitting || mismatch || !currentPassword || !newPassword || !confirmNewPassword}
              >
                {submitting ? t("common.saving") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingSection>
  );
};

export default Security;
